import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import {
    CircularProgress, TextField, Button, Accordion, AccordionSummary, Alert,
    AccordionDetails, Checkbox, FormControlLabel, MenuItem, Typography, Box, IconButton, Stack, Grid, RadioGroup, Radio, Switch
} from '@mui/material';
import { ExpandMore, Delete } from '@mui/icons-material';

import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useErrorManager from "../../hooks/useErrorManager";

import PageContainer from '../Layout/PageContainer';
import ConfirmationDialog from '../Layout/ConfirmationDialog';
import ProfileDestinationAccordion from './ProfileDestinationAccordion';

import { fwdDataTypes } from '../../constants/fwdDataTypes';
import { deviceTypes } from '../../constants/deviceTypes';
import { xmlVersions } from '../../constants/xmlVersions';
import { repFormats } from '../../constants/repFormats';
import { repUnits } from '../../constants/repUnits';
import { dmEncryptionTypes } from '../../constants/dmEncryptionTypes';
import { dmManufacturers } from '../../constants/dmManufacturers';

import { useNotification } from '../../contexts/NotificationContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';

const StyledError = styled(Typography)`
  text-align: center;
  margin-top: 10px;
`;

const StyledFormTextField = styled(TextField)`
  margin-top: 4px;
  margin-bottom: 4px;
`;

function EditFwdProfile() {
    const { profileId } = useParams();

    const hardcodedSettings = {
        repFormat: "REP_EN",
        repUnit: "Dynamic",
        xmlVersion: "XMLv1_1",
        dmManufacturerFilter: "AllDevices",
        dmEncryptionFilter: "AllDevices",
    }

    const [fwdProfile, setFwdProfile] = useState({
        profileName: '',
        dataType: '',
        profileType: 'Global',
        repFormat: hardcodedSettings.repFormat,
        repUnit: hardcodedSettings.repUnit,
        xmlVersion: hardcodedSettings.xmlVersion,
        dmManufacturerFilter: hardcodedSettings.dmManufacturerFilter,
        dmEncryptionFilter: hardcodedSettings.dmEncryptionFilter,
        fileFormats: { rep: false, xml: false, rawCSV: false },  
        excludedDeviceTypes: [],
        destinations: [],
    });

    

    const [formErrors, setFormErrors] = useState({});

    const [loading, setLoading] = useState(true);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const { showNotification } = useNotification();

    const { setError, clearError, getError } = useErrorManager();
    const { updateBreadcrumb } = useBreadcrumb();
    
    const navigate = useNavigate();
    const axiosInstance = useAxiosPrivate();  



    const validateForm = (profile) => {
        const errors = {};
    
        // Main profile-level validations:
        if (!profile.profileName.trim()) {
          errors.profileName = "Profile Name is required.";
        }
        if (!profile.dataType) {
          errors.dataType = "Action Type is required.";
        }
         // Helper function to validate email format.
        const isValidEmail = (email) => {
            // A simple regex for email validation.
            const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return regex.test(email);
        };

        // Helper function to validate IPv4 address.
        const isValidIP = (ip) => {
            // This regex checks for four octets, each 0-255.
            const regex = /^(25[0-5]|2[0-4]\d|[01]?\d\d?)(\.(25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/;
            return regex.test(ip);
        };

         // Validate fileFormats conditionally based on dataType.
        if (profile.dataType === "QnodeReadout") {
            if ( !profile.fileFormats.rep && !profile.fileFormats.xml && !profile.fileFormats.rawCSV ) {
                errors.fileFormats = "At least one file format must be selected.";
            }
        } else if (profile.dataType === "DMReadout") {
            if (!profile.fileFormats.xml && !profile.fileFormats.rawCSV) {
                errors.fileFormats = "At least one file format must be selected.";
            }
        }



        if(profile.destinations.length <= 0){
            errors.destinations = "At least one destination is needed.";
        }

        
        // Validate each destination.
        profile.destinations.forEach((dest, index) => {

            let destHasError = false;


            if (dest.message_type === 'email') {
                if (!dest.email || !dest.email.trim()) {
                    errors[`destinations.${index}.email`] = "Email Address is required.";
                    destHasError = true;
                } else if (!isValidEmail(dest.email)) {
                    errors[`destinations.${index}.email`] = "Invalid email format.";
                    destHasError = true;
                }
                if (!dest.attachment_password || !dest.attachment_password.trim()) {
                    errors[`destinations.${index}.attachment_password`] = "Attachment Password is required.";
                    destHasError = true;
                }
                // if ( !dest.attachment_password ) {
                //     console.log("DASDASDASD");
                //     updateDestination(index, "attachment_password", null);
                // }
            } else if (dest.message_type === 'SFTP') {
                if (!dest.ip_address || !dest.ip_address.trim()) {
                    errors[`destinations.${index}.ip_address`] = "IP Address is required.";
                    destHasError = true;
                } else if (!isValidIP(dest.ip_address)) {
                    errors[`destinations.${index}.ip_address`] = "Invalid IP address format.";
                    destHasError = true;
                }
                if (!dest.tcp_port || String(dest.tcp_port).trim() === "") {
                    errors[`destinations.${index}.tcp_port`] = "Port is required.";
                    destHasError = true;
                }
                if (!dest.user_name || !dest.user_name.trim()) {
                    errors[`destinations.${index}.user_name`] = "Username is required.";
                    destHasError = true;
                }
                if (dest.auth_type === 'Password' && (!dest.ftp_password || !dest.ftp_password.trim())) {
                    errors[`destinations.${index}.ftp_password`] = "Password is required.";
                }
                if (dest.auth_type === 'Key') {
                    if (!dest.private_key || !dest.private_key.trim()) {
                        errors[`destinations.${index}.private_key`] = "Private key is required.";
                        destHasError = true;
                    }
                    if (dest.private_key_encrypted && (!dest.private_key_password || !dest.private_key_password.trim())) {
                        errors[`destinations.${index}.private_key_password`] = "Private key password is required.";
                    }
                }
            }
            if (destHasError) {
                errors[`destinations.${index}.alert`] = `Invalid fields in Destination ${index + 1}`;
            }
        });
    
        return errors;
    };





    const fetchProfileDetails = async () => {
        try {
            const response = await axiosInstance.get(`/fwd/profiles/${profileId}/details`);

            const hardcodeField = (field) => response.data[field] == null ||  response.data[field] === "SystemDefault" ? hardcodedSettings[field] : response.data[field]; 

            setFwdProfile({
                profileName: response.data.name,
                dataType: response.data.dataType,
                profileType: response.data.profileType,
                repFormat: hardcodeField("repFormat"),
                repUnit: hardcodeField("repUnit"),
                xmlVersion: hardcodeField("xmlVersion"),
                dmManufacturerFilter: hardcodeField("dmManufacturerFilter"),
                dmEncryptionFilter: hardcodeField("dmEncryptionFilter"),
                fileFormats: {
                    rep: response.data.fileFormats.includes('REP'),
                    xml: response.data.fileFormats.includes('XML'),
                    rawCSV: response.data.fileFormats.includes('RAWCSV'),
                },
                excludedDeviceTypes: response.data.excludedDeviceTypes,
                destinations: response.data.forwardProfileEntryBL || [], // If API returns destinations
            });
            clearError('fetch');
            setLoading(false);
        } catch (error) {
            setError('fetch', error.message);
            setLoading(false);
        }
    };


    useEffect(() => {
        if (profileId) {
            fetchProfileDetails();
            updateBreadcrumb([
                { label: 'Manage Forwarding Profiles', link: '/profiles' },
                { label: 'Edit' },
            ]);
        } else {    
            setLoading(false);
            updateBreadcrumb([
                { label: 'Manage Forwarding Profiles', link: '/profiles' },
                { label: 'Create' },
            ]);
        }
    }, [profileId]);


    
    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm(fwdProfile);
        setFormErrors(errors);
        if (Object.keys(errors).length > 0) {
            return;
        }
        try {
            const profileObj = {
                name: fwdProfile.profileName,
                dataType: fwdProfile.dataType,
                profileType: fwdProfile.profileType,
                active: true,
                repFormat: fwdProfile.repFormat,
                repUnit: fwdProfile.repUnit,
                xmlVersion: fwdProfile.xmlVersion,
                dmManufacturerFilter: fwdProfile.dmManufacturerFilter,
                dmEncryptionFilter: fwdProfile.dmEncryptionFilter,
                fileFormats: [
                    fwdProfile.fileFormats.rep && 'REP',
                    fwdProfile.fileFormats.xml && 'XML',
                    fwdProfile.fileFormats.rawCSV && 'RAWCSV'
                ].filter(Boolean),
                excludedDeviceTypes: fwdProfile.excludedDeviceTypes,
                forwardProfileEntryBL: fwdProfile.destinations,//.map(dest => ({ ...dest })), // Send destinations as-is
            };
            if (profileId) {
                profileObj.id = profileId;
                await axiosInstance.put(`/fwd/profiles/${profileId}/edit`, profileObj);
            } else {
                await axiosInstance.post('/fwd/profiles/create', profileObj);
            }
            clearError('edit');
            navigate(`/profiles`, { state: { success: 'Forwarding profile saved successfully' } });
        } catch (error) {
            if (error.response) {
                // Server responded with a status code outside the 2xx range
                const status = error.response.status;
                // const serverMessage = error.response.data?.message;
    
                switch (status) {
                    case 409:
                        setError('edit', 'Profile with this name already exists.');
                        break;
                    case 400:
                        setError('edit', 'Invalid request. Please check your input.');
                        break;
                    case 500:
                        setError('edit', 'Server error. Please try again later.');
                        break;
                    default:
                        setError('edit', 'An unexpected error occurred.');
                        break;
                }
            } else if (error.request) {
                // Request was made but no response was received
                setError('edit', 'Network error. Please check your connection and try again.');
            } else {
                // Something happened in setting up the request
                setError('edit', error.message || 'An unexpected error occurred.');
            }
        }
    };

    const handleChange = (field, value) => {
        setFwdProfile(prevState => ({ ...prevState, [field]: value }));
    };


    const handleDialogOpen = () => setOpenConfirmDialog(true); // Open the confirmation dialog
    const handleDialogClose = () => setOpenConfirmDialog(false); // Close the dialog


    const handleFwdProfileDelete = async () => {
        try {
            await axiosInstance.delete(`/fwd/profiles/${profileId}`);
            clearError('delete');
            handleDialogClose();

            navigate(`/profiles`, { state: { success: 'Profile deleted successfully' }} )

        } catch (error) {
            setError('delete', error);
            showNotification('Failed to delete profile','error')
            handleDialogClose();
        }
    };

    const handleFileFormatsChange = (field) => {
        setFwdProfile(prevState => ({
            ...prevState,
            fileFormats: {
                ...prevState.fileFormats,
                [field]: !prevState.fileFormats[field],
            },
        }));
    };

    // Destination Addresses Handling
    const addDestination = () => {
        if (fwdProfile.destinations.length < 5) {
            setFwdProfile(prevState => ({
                ...prevState,
                destinations: [
                    ...prevState.destinations,
                    { 
                        //EMAIL
                        message_type: 'email', //email || SFTP
                        email: null, 
                        attachment_password: null, 
                        //SFTP
                        ip_address: null, 
                        user_name: null, 
                        remote_directory: null, 
                        tcp_port: null,
                        ftp_password: null,
                        transfer_ready_file: false,
                        use_partial_file_extension: false,
                        compress_attachment: false,
                        verify_host_key: false,
                        host_key_fingerprint: null,
                        private_key: null,
                        private_key_encrypted: false,
                        private_key_password: null,
                        auth_type: "Password"
                    }
                ],
            }));
        }
    };

    const removeDestination = (index) => {
        setFwdProfile(prevState => ({
            ...prevState,
            destinations: prevState.destinations.filter((_, i) => i !== index),
        }));
    };

    const updateDestination = (index, field, value) => {
        setFwdProfile(prevState => {
            const updatedDestinations = [...prevState.destinations];
            updatedDestinations[index] = {
                ...updatedDestinations[index],
                [field]: value,
            };
            return { ...prevState, destinations: updatedDestinations };
        });
    };


    return (
        loading ? (
            <CircularProgress />
        ) : getError('fetch') ? (
            <Typography variant="h7"> {getError('fetch')}</Typography>
        ) : (
            <>
                <form onSubmit={handleSubmit}>
                    
                    <PageContainer>
                        <Grid container spacing={1}>

                            <Grid item xs={4}>
                                <TextField
                                    label="Profile Name"
                                    value={fwdProfile.profileName}
                                    onChange={(e) => handleChange('profileName', e.target.value)}
                                    fullWidth
                                    required
                                    margin="normal"
                                    error={Boolean(formErrors.profileName)}
                                    helperText={formErrors.profileName}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    label="Action Type"
                                    select
                                    value={fwdProfile.dataType}
                                    // onChange={(e) => handleChange('dataType', e.target.value)}
                                    onChange={(e) => setFwdProfile(prevState => {
                                          return {
                                            ...prevState,
                                            dataType: e.target.value,
                                            // Reset fileFormats to false when changing the Action Type.
                                            fileFormats: { rep: false, xml: false, rawCSV: false },
                                          }
                                      })
                                    }
                                    fullWidth
                                    required
                                    margin="normal"
                                    error={Boolean(formErrors.dataType)}
                                    helperText={formErrors.dataType}
                                >
                                    <MenuItem value="">Select action type</MenuItem>
                                    {Object.entries(fwdDataTypes).map(([key, label]) => (
                                        <MenuItem key={key} value={key}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    label="Profile Type"
                                    select
                                    value={fwdProfile.profileType}
                                    onChange={(e) => handleChange('profileType', e.target.value)}
                                    fullWidth
                                    margin="normal"
                                >
                                    <MenuItem value="Global">Global</MenuItem>
                                    <MenuItem value="Temporary">Temporary</MenuItem>
                                </TextField>
                            </Grid>


                            {/* Data Format IF READOUT */}
                            {(fwdProfile.dataType === 'DMReadout' || fwdProfile.dataType === 'QnodeReadout' )&& (
                                <Grid item xs={12} container spacing={1}>
                                    <Grid item xs={12}>
                                        
                                        <Typography>Data Format</Typography>

                                        {/* <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={
                                                        !fwdProfile.fileFormats.rep &&
                                                        !fwdProfile.fileFormats.xml &&
                                                        !fwdProfile.fileFormats.rawCSV
                                                    } // True if all file formats are unchecked
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            // Uncheck all individual file formats if System Settings is checked
                                                            setFwdProfile((prevState) => ({
                                                                ...prevState,
                                                                fileFormats: { rep: false, xml: false, rawCSV: false },
                                                            }));
                                                        }
                                                    }}
                                                />
                                            }
                                            label="System Settings"
                                        /> */}

                                        <FormControlLabel
                                            control={<Checkbox checked={fwdProfile.fileFormats.xml} onChange={() => handleFileFormatsChange('xml')} />}
                                            label="XML"
                                        />

                                        {fwdProfile.dataType === 'QnodeReadout'  && (
                                            <FormControlLabel
                                                control={<Checkbox checked={fwdProfile.fileFormats.rep} onChange={() => handleFileFormatsChange('rep')} />}
                                                label="REP"
                                            />
                                        )}
                                        
                                        
                                        <FormControlLabel
                                            control={<Checkbox checked={fwdProfile.fileFormats.rawCSV} onChange={() => handleFileFormatsChange('rawCSV')} />}
                                            label="RAW/CSV"
                                        />

                                    </Grid>

                                    {/* IF XML */}
                                    {fwdProfile.fileFormats.xml  && (
                                        <Grid item xs={4}>
                                            <StyledFormTextField
                                                label="XML Version"
                                                select
                                                value={fwdProfile.xmlVersion}
                                                onChange={(e) => handleChange('xmlVersion', e.target.value)}
                                                fullWidth
                                                margin="normal"
                                            >
                                                {Object.entries(xmlVersions).map(([key, label]) => (
                                                    <MenuItem key={key} value={key}>
                                                        {label}
                                                    </MenuItem>
                                                ))}
                                            </StyledFormTextField>
                                        </Grid>
                                    )}

                                    {/* IF REP */}
                                    {fwdProfile.fileFormats.rep  && fwdProfile.dataType === 'QnodeReadout' && (
                                        <Grid item xs={12}  container spacing={1}>
                                            <Grid item xs={4}>
                                                <StyledFormTextField
                                                    label="REP Format"
                                                    select
                                                    value={fwdProfile.repFormat}
                                                    onChange={(e) => handleChange('repFormat', e.target.value)}
                                                    fullWidth
                                                    margin="normal"
                                                >
                                                {Object.entries(repFormats).map(([key, label]) => (
                                                        <MenuItem key={key} value={key}>
                                                            {label}
                                                        </MenuItem>
                                                    ))}
                                                </StyledFormTextField>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <StyledFormTextField
                                                    label="REP Unit conversion"
                                                    select
                                                    value={fwdProfile.repUnit}
                                                    onChange={(e) => handleChange('repUnit', e.target.value)}
                                                    fullWidth
                                                    margin="normal"
                                                >
                                                    {Object.entries(repUnits).map(([key, label]) => (
                                                        <MenuItem key={key} value={key}>
                                                            {label}
                                                        </MenuItem>
                                                    ))}
                                                </StyledFormTextField>
                                            </Grid>
                                        </Grid>
                                    )}

                                    {fwdProfile.dataType === 'DMReadout'  && (
                                        <Grid item xs={12}  container spacing={1}>
                                            <Grid item xs={4}>
                                                <StyledFormTextField
                                                    label="Manufacturer"
                                                    select
                                                    value={fwdProfile.dmManufacturerFilter}
                                                    onChange={(e) => handleChange('dmManufacturerFilter', e.target.value)}
                                                    fullWidth
                                                    margin="normal"
                                                >
                                                {Object.entries(dmManufacturers).map(([key, label]) => (
                                                        <MenuItem key={key} value={key}>
                                                            {label}
                                                        </MenuItem>
                                                    ))}
                                                </StyledFormTextField>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <StyledFormTextField
                                                    label="Encryption filter"
                                                    select
                                                    value={fwdProfile.dmEncryptionFilter}
                                                    onChange={(e) => handleChange('dmEncryptionFilter',e.target.value)}
                                                    fullWidth
                                                    margin="normal"
                                                >
                                                    {Object.entries(dmEncryptionTypes).map(([key, label]) => (
                                                        <MenuItem key={key} value={key}>
                                                            {label}
                                                        </MenuItem>
                                                    ))}
                                                </StyledFormTextField>
                                            </Grid>
                                        </Grid>
                                    )}



                                    <Grid item xs={4}>
                                        <StyledFormTextField
                                            label="Exclude Device Types"
                                            select
                                            SelectProps={{
                                                multiple: true, // Enable multiple selection
                                                renderValue: (selected) => {
                                                    // Ensure selected is always an array
                                                    if (!Array.isArray(selected)) return '';
                                                    return selected.join(', '); // Display as comma-separated string
                                                },
                                            }}
                                            value={fwdProfile.excludedDeviceTypes || []}
                                            onChange={(e) => handleChange('excludedDeviceTypes', e.target.value)}
                                            fullWidth
                                            margin="normal"
                                        >
                                            <MenuItem value="">Select action type</MenuItem>
                                            {Object.entries(deviceTypes).map(([key, label]) => (
                                                <MenuItem key={key} value={key}>
                                                    <Checkbox checked={fwdProfile.excludedDeviceTypes?.includes(key)} />
                                                    <Typography>{`${label} (${key})`}</Typography>
                                                </MenuItem>
                                            ))}
                                        </StyledFormTextField>
                                    </Grid>

                                </Grid>
                                
                                
                            )}

                        </Grid>
                    </PageContainer>

                    {/* Destination Addresses */}
                    {fwdProfile.destinations.map((destination, index) => (
                        <ProfileDestinationAccordion
                            key={index}
                            destination={destination}
                            index={index}
                            updateDestination={updateDestination}
                            removeDestination={removeDestination}
                            errors={formErrors}
                      />
                    ))}

                    {/* Add Destination Button */}
                    <Box mt={2} mb={2}>
                        <Button variant="contained" color="primary" onClick={addDestination} disabled={fwdProfile.destinations.length >= 5}>
                            Add Destination Address
                        </Button>
                    </Box>




                    {formErrors.fileFormats && (
                        <Alert 
                            severity="error" 
                            sx={{ display: 'flex', alignItems: 'center', borderRadius: 2 }}
                        >
                            {formErrors.fileFormats}
                        </Alert>
                    )}

                    {formErrors.destinations && (
                        <Alert 
                            severity="error" 
                            sx={{ display: 'flex', alignItems: 'center', borderRadius: 2 }}
                        >
                            {formErrors.destinations}
                        </Alert>
                    )}

                    {fwdProfile.destinations.map((dest, index) => (
                        formErrors[`destinations.${index}.alert`] && (
                            <Alert 
                            key={`dest-alert-${index}`}
                            severity="error" 
                            sx={{ display: 'flex', alignItems: 'center', borderRadius: 2, marginTop: 1 }}
                            >
                            {formErrors[`destinations.${index}.alert`]}
                            </Alert>
                        )
                    ))}


                    {/* Submit and Error */}
                    {profileId ? (
                    <>
                        <Grid container spacing={2}>
                                <Grid item xs={11}>
                                    <Button type="submit" variant="contained" color="primary" fullWidth>
                                        Save
                                    </Button>
                                </Grid>
                                <Grid item xs={1}>
                                    <IconButton
                                        onClick={() => handleDialogOpen()}
                                        color="error"
                                    >
                                        <Delete />
                                    </IconButton>
                                </Grid>
                        </Grid>
                        <ConfirmationDialog
                            open={openConfirmDialog}
                            onClose={handleDialogClose}
                            onConfirm={handleFwdProfileDelete}
                            title="Confirm Deletion"
                            description="Are you sure you want to delete this profile? This action cannot be undone."
                            confirmText="Delete"
                            cancelText="Cancel"
                        />
                    </>
                    ) : (
                        <Button type="submit" variant="contained" color="primary" fullWidth>
                            Create
                        </Button>
                    )
                    }
                    {getError('edit') && <StyledError variant="body2">{getError('edit')}</StyledError>}
                </form>
            </>
        )
    );
}

export default EditFwdProfile;
